// /src/pages/LoginPage.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert, 
  Collapse, 
  IconButton,
  Paper,
  InputAdornment,
  Fade,
  Container
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { 
  signInWithEmailAndPassword 
} from 'firebase/auth';
import { auth } from '../firebase/firebaseConfig';
import { logActivity } from '../api/apiService';
import { 
  Close, 
  Email, 
  Lock, 
  Visibility, 
  VisibilityOff,
} from '@mui/icons-material';
import wteLogo from '../assets/wte_logo.png';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 12px rgba(0,0,0,0.08)',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  width: '100%',
  maxWidth: '420px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #0A84FF 0%, #64B5F6 100%)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,
  borderRadius: '8px',
  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
  '&:hover': {
    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.24)}`,
  }
}));

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleLogIn = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('Calling logActivity for user:', user.email);
      try {
        await logActivity(user.email);
      } catch (err) {
        console.error('Error logging activity to DynamoDB:', err);
      }
      setError(null);
      setSuccess(true);
      setOpenAlert(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      let errorMessage = 'An unknown error occurred!';
      if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid credentials. Please try again.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      }
      setError(errorMessage);
      setSuccess(false);
      setOpenAlert(true);
    }
  };

  return (
    <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: (theme) => theme.palette.background.default,
          padding: { xs: 2, sm: 4, md: 6, lg: 8 },
          width: '100%',
        }}
      >
        <Container 
          maxWidth="lg" 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            px: { xs: 2, sm: 4, md: 6, lg: 8 },
            width: '100%',
          }}
        >
          <Fade in timeout={1000}>
            <Box sx={{ 
              textAlign: 'center', 
              mb: { xs: 2, sm: 4, md: 6 },
              width: '100%',
              maxWidth: { sm: '600px', md: '800px', lg: '1000px' }
            }}>
              <Box component="img" src={wteLogo} alt="WTE Logo" sx={{ display: 'block', mx: 'auto', width: { xs: '60px', sm: '80px', md: '100px' }, mb: { xs: 1, sm: 2, md: 3 } }} />
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  fontWeight: 600,
                  color: (theme) => theme.palette.text.primary,
                  mb: 1,
                  fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem', lg: '3rem' }
                }}
              >
                Welcome Back
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem', lg: '1.25rem' }
                }}
              >
                Please log in to your account
              </Typography>
            </Box>
          </Fade>

          <Fade in timeout={1000} style={{ transitionDelay: '200ms' }}>
            <StyledPaper 
              elevation={0}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '420px', md: '480px', lg: '520px' },
                margin: '0 auto',
                padding: { xs: 2, sm: 4, md: 6 }
              }}
            >
              <Collapse in={openAlert}>
                <Alert
                  severity={success ? 'success' : 'error'}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setOpenAlert(false)}
                    >
                      <Close fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ 
                    mb: { xs: 1.5, sm: 2, md: 3 }, 
                    borderRadius: '8px',
                    '& .MuiAlert-message': {
                      flex: 1,
                    }
                  }}
                >
                  {success
                    ? 'Login successful! Redirecting to the dashboard...'
                    : error || 'An unknown error occurred!'}
                </Alert>
              </Collapse>

              <Box
                component="form"
                onSubmit={handleLogIn}
                sx={{ 
                  width: '100%',
                  '& .MuiTextField-root': {
                    mb: { xs: 1.5, sm: 2, md: 3 }
                  }
                }}
              >
                <StyledTextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  margin="normal"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Email address"
                  autoComplete="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: (theme) => theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <StyledTextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  margin="normal"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-label="Password"
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: (theme) => theme.palette.primary.main }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ '&:focus-visible': { outline: '2px solid #0A84FF', outlineOffset: 2, borderRadius: '6px' } }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <StyledButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  aria-label="Log in"
                  sx={{ 
                    mt: { xs: 2, sm: 3, md: 4 },
                    mb: { xs: 1.5, sm: 2, md: 3 },
                    height: { xs: '40px', sm: '44px', md: '48px' },
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                    '&:hover': { backgroundColor: (theme) => theme.palette.primary.dark }
                  }}
                >
                  Log In
                </StyledButton>
              </Box>

              <Box sx={{ 
                textAlign: 'center', 
                mt: { xs: 1.5, sm: 2, md: 3 }
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                    '& a': {
                      color: (theme) => theme.palette.primary.main,
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      }
                    }
                  }}
                >
                  Don't have an account?{' '}
                  <Link to="/signup">
                    Register here
                  </Link>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1.5,
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    '& a': {
                      color: (theme) => theme.palette.primary.main,
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': { textDecoration: 'underline' },
                    },
                  }}
                >
                  <Link to="/privacy-policy">Privacy Policy</Link>
                </Typography>
              </Box>
            </StyledPaper>
          </Fade>
        </Container>
      </Box>
  );
};

export default LoginPage;
